import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { loadDraft, saveDraft, clearDraft, purgeExpiredDrafts } from '../utils/historiaDraft';

// Espera tras la última tecla antes de escribir el borrador en localStorage.
export const AUTOSAVE_DELAY_MS = 2000;

/**
 * Autoguardado del formulario de historia clínica (ver utils/historiaDraft.js).
 *
 * @param {object} params
 * @param {string} params.userId         id del médico (sin él no se guarda nada)
 * @param {string} params.appointmentId  id de la cita
 * @param {object} params.formData       estado actual del formulario
 * @param {object} params.initialFormData estado con el que se abrió: si formData
 *        es igual, no hay cambios sin guardar
 *
 * @returns {{
 *   pendingDraft: {formData: object, savedAt: number} | null,
 *   dirty: boolean,
 *   restore: () => object | null,
 *   discard: () => void,
 *   markSaved: () => void,
 *   flush: () => void,
 * }}
 *
 * El borrador encontrado al abrir se lee una vez y queda en memoria
 * (pendingDraft) hasta que el médico elige "Recuperar" o "Descartar". Si empieza
 * a escribir sin decidir, el autoguardado sigue protegiendo lo que escribe
 * ahora (aunque reemplace el borrador en localStorage); el anterior sigue
 * disponible en memoria para recuperarlo mientras el formulario esté abierto.
 */
export default function useHistoriaDraft({ userId, appointmentId, formData, initialFormData }) {
  const [pendingDraft, setPendingDraft] = useState(() => {
    // De paso se limpian los borradores caducados de otras citas: son datos
    // clínicos en texto plano y no deben quedarse en el navegador sin motivo.
    purgeExpiredDrafts();
    return loadDraft(userId, appointmentId);
  });

  const current = useMemo(() => JSON.stringify(formData), [formData]);
  const initial = useMemo(() => JSON.stringify(initialFormData), [initialFormData]);

  // La línea base cambia al abrir con otros datos y al guardar con éxito. Se
  // lleva también en un ref: flush() puede correr en el desmontaje, antes de que
  // React procese el setState de markSaved().
  const [baseline, setBaseline] = useState(initial);
  const baselineRef = useRef(initial);
  useEffect(() => {
    baselineRef.current = initial;
    setBaseline(initial);
  }, [initial]);

  const dirty = current !== baseline;

  const latestRef = useRef({});
  latestRef.current = { userId, appointmentId, formData, current, pendingDraft };

  // Autoguardado con debounce.
  useEffect(() => {
    if (!dirty) return undefined;
    const timer = setTimeout(() => {
      saveDraft(userId, appointmentId, formData);
    }, AUTOSAVE_DELAY_MS);
    return () => clearTimeout(timer);
  }, [current, dirty, userId, appointmentId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Guarda ya, sin esperar al debounce, si hay cambios pendientes.
  const flush = useCallback(() => {
    const l = latestRef.current;
    if (l.current === baselineRef.current) return;
    saveDraft(l.userId, l.appointmentId, l.formData);
  }, []);

  // Si la página se recarga o se va a /login, el debounce no llega a vencer.
  useEffect(() => {
    window.addEventListener('pagehide', flush);
    window.addEventListener('beforeunload', flush);
    return () => {
      window.removeEventListener('pagehide', flush);
      window.removeEventListener('beforeunload', flush);
      // Al desmontar (modal cerrado) también se conserva lo último escrito.
      flush();
    };
  }, [flush]);

  const restore = useCallback(() => {
    const draft = latestRef.current.pendingDraft;
    setPendingDraft(null);
    return draft ? draft.formData : null;
  }, []);

  const discard = useCallback(() => {
    const l = latestRef.current;
    clearDraft(l.userId, l.appointmentId);
    setPendingDraft(null);
  }, []);

  // Tras guardar en el servidor: el borrador sobra y lo actual pasa a ser la base.
  const markSaved = useCallback(() => {
    const l = latestRef.current;
    clearDraft(l.userId, l.appointmentId);
    baselineRef.current = l.current;
    setBaseline(l.current);
    setPendingDraft(null);
  }, []);

  return { pendingDraft, dirty, restore, discard, markSaved, flush };
}
